import ArrowBackIosNewRounded from "@mui/icons-material/ArrowBackIosNewRounded";
import { Box, Button, Stack } from "@mui/material";
import { useState } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import { Proposal } from "../../types/proposal.types";
import { generateProposalPDF } from "../../utils/pdf-generator";
import ActivityLogTable from "./components/view-proposal-page/ActivityLogTable";
import PdfHeader from "./components/view-proposal-page/PdfHeader";
import ProposalPagesPreview from "./components/view-proposal-page/ProposalPagesPreview";
import ProposalStatusSidebar from "./components/view-proposal-page/ProposalStatusSideBar";
import { CoverPageTemplate } from "../../types/cover.types";

type ProposalStatus = "Accepted" | "Sent" | "Declined" | "Final" | "Draft";

const PROPOSAL_PAGES = [
  { id: "cover", label: "Cover Page" },
  { id: "main", label: "Letter & Overview" },
  { id: "memorial", label: "Memorial Page" },
  { id: "frequently", label: "Frequently Used Services" },
  { id: "services", label: "Included Services" },
  { id: "service", label: "Services Page" },
  { id: "whatsapp", label: "WhatsApp Preview" },
  { id: "agreement", label: "Agreement Form" },
  { id: "backpage", label: "Back Page" },
];

interface ViewProposalPageProps {
  proposal: Proposal;
  status?: ProposalStatus;
  onBack?: () => void;
  onUpdateProposal?: () => void;
  onStatusChange?: (id: number, status: string) => void;
  onGenerateInvoice?: () => void;
  onConfirmBooking?: () => void;
  previousStreams?: { id?: string; title: string; url: string }[];
  selectedCoverTemplate?: CoverPageTemplate | null;
  // previewImageUrl?: string;
  // thumbnailUrls?: string[];
}

export default function ViewProposalPage({
  proposal,
  status,
  onBack,
  onUpdateProposal,
  onStatusChange,
  onGenerateInvoice,
  onConfirmBooking,
  previousStreams,
  selectedCoverTemplate,
}: ViewProposalPageProps) {
  const [currentStatus, setCurrentStatus] = useState<ProposalStatus>(
    status ?? "Draft",
  );
  const [pageSelectionOpen, setPageSelectionOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [mode, setMode] = useState<"download" | "print">("download");

  const createdBy =
    `${proposal.created_by?.first_name ?? ""} ${proposal.created_by?.last_name ?? ""}`.trim() ||
    "N/A";
  const updatedBy =
    `${proposal.last_updated_by?.first_name ?? ""} ${proposal.last_updated_by?.last_name ?? ""}`.trim() ||
    "N/A";

  const lastUpdated =
    proposal.updated_at && proposal.updated_at !== proposal.created_at
      ? {
          date: new Date(proposal.updated_at).toLocaleDateString(),
          by: updatedBy,
        }
      : undefined;

  const activityLog = [
    {
      date: proposal.created_at
        ? new Date(proposal.created_at).toLocaleDateString()
        : "",
      status: currentStatus,
      by: createdBy,
    },
  ];


  const handleDownload = () => {
  setMode("download");
  setPageSelectionOpen(true);
};

const handlePrint = () => {
  setMode("print");
  setPageSelectionOpen(true);
};

const handleConfirmDownload = async (selectedPages: string[]) => {
  const pagesInOrder = PROPOSAL_PAGES.filter((p) =>
    selectedPages.includes(p.id),
  ).map((p) => p.id);

  try {
    setIsGenerating(true);
    if (mode === "print") {
      await generateProposalPDF(proposal, false, pagesInOrder, true);
    } else {
      await generateProposalPDF(proposal, false, pagesInOrder);
    }
    showSuccessToast(mode === "print" ? "Sent to printer!" : "PDF downloaded successfully!");
    setPageSelectionOpen(false);
  } catch {
    showErrorToast(mode === "print" ? "Error printing" : "Error generating PDF");
  } finally {
    setIsGenerating(false);
  }
};

  return (
    <Box>
      {/* Top bar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2.5 }}
      >
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIosNewRounded sx={{ fontSize: 13 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            color: "#475569",
            p: 0,
            "&:hover": { bgcolor: "transparent", color: "#724e41" },
          }}
        >
          Back to all Proposals
        </Button>

        <Button
          onClick={onUpdateProposal}
          sx={{
            bgcolor: "#724e41",
            color: "white",
            borderRadius: "10px",
            px: 3,
            height: 38,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "13px",
            "&:hover": { bgcolor: "#5d3e33" },
          }}
        >
          Update Proposal
        </Button>
      </Stack>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PdfHeader
            onPrint={handlePrint}
            onShare={() => {}}
            onDownload={handleDownload}
            bookingType={proposal.booking?.booking_type}
          />
          <ProposalPagesPreview
            selectedCoverTemplate={selectedCoverTemplate}
            proposal={proposal}
            previousStreams={previousStreams}
            pageSelectionOpen={pageSelectionOpen}
            isGenerating={isGenerating}
            onClosePageSelection={() => setPageSelectionOpen(false)}
            onConfirmDownload={handleConfirmDownload}
          />
          <ActivityLogTable logs={activityLog} lastUpdated={lastUpdated} />
        </Box>

        {/* RIGHT: Status sidebar */}
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <ProposalStatusSidebar
            status={currentStatus}
            proposalId={proposal.id}
            onStatusChange={(newStatus) => {
              setCurrentStatus(newStatus);
              onStatusChange?.(proposal.id, newStatus);
            }}
            onGenerateInvoice={onGenerateInvoice}
            onConfirmBooking={onConfirmBooking}
          />
        </Box>
      </Box>
    </Box>
  );
}
